import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { BurrowNav } from '../components/BurrowNav'
import { useToast } from '../components/Toast'
import { btn, chip, errorText, Loading } from '../components/ui'
import { useSession } from '../lib/session'
import { useDismiss, useNow } from '../lib/hooks'
import { reflectionBlocks, REFLECTION_SUBTITLE } from '../lib/blocks'
import { countWords, dateKey, readMinutes, shortDate, shortWeekdayDate, startOfDay, timeAgo, weekdayDate } from '../lib/text'
import type { Writing } from '../lib/types'

type Filter = 'all' | 'drafts' | 'published' | 'reflections'

function rowMeta(w: Writing, now: Date): string {
  const minutes = readMinutes(w.word_count)
  const narrated = w.narration ? ' · narrated' : ''
  if (w.status === 'draft') return `Draft · edited ${timeAgo(w.updated_at, now)} · ${w.word_count.toLocaleString()} words${narrated}`
  const date = shortDate(w.published_at ?? w.updated_at)
  return (w.kind === 'reflection' ? `Reflection · ${date} · ${minutes} min` : `${date} · ${minutes} min`) + narrated
}

function statusLabel(w: Writing): string {
  if (w.status === 'draft') return 'Draft'
  if (w.visibility === 'link') return 'Link only'
  if (w.visibility === 'followers') return 'Thinking Along with You'
  return 'Published'
}

export function WritingsIndex() {
  const { backend, profile } = useSession()
  const navigate = useNavigate()
  const toast = useToast()
  const now = useNow(60_000)
  const [writings, setWritings] = useState<Writing[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [todayCount, setTodayCount] = useState<number | null>(null)
  const [filter, setFilter] = useState<Filter>('all')
  const [menuOpen, setMenuOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const closeMenu = useCallback(() => setMenuOpen(false), [])
  useDismiss(menuRef, menuOpen, closeMenu)

  useEffect(() => {
    document.title = 'Writings — Burrow'
    let alive = true
    backend
      .listMyWritings()
      .then((w) => alive && setWritings(w))
      .catch((e: unknown) => alive && setError(errorText(e)))
    backend
      .thoughtCountsByDay(1)
      .then((c) => alive && setTodayCount(c[0] ?? 0))
      .catch(() => alive && setTodayCount(0))
    return () => {
      alive = false
    }
  }, [backend])

  async function startBlank() {
    setCreating(true)
    try {
      const w = await backend.createWriting({})
      navigate(`/w/${w.id}`)
    } catch (e) {
      toast(errorText(e))
      setCreating(false)
    }
  }

  async function startReflection() {
    setCreating(true)
    try {
      const thoughts = await backend.listThoughts({ day: 0, limit: 100 })
      const withImages = await Promise.all(
        thoughts.map(async (thought) => ({
          thought,
          imageSrc: thought.image_path ? await backend.importThoughtImage(thought.image_path) : null,
        })),
      )
      const today = startOfDay(0)
      const title = weekdayDate(today)
      const blocks = reflectionBlocks(withImages)
      const w = await backend.createWriting({
        kind: 'reflection',
        title,
        subtitle: REFLECTION_SUBTITLE,
        blocks,
        reflection_date: dateKey(today),
        word_count: countWords(title, blocks),
      })
      navigate(`/w/${w.id}`)
    } catch (e) {
      toast(errorText(e))
      setCreating(false)
    }
  }

  async function remove(id: string) {
    try {
      await backend.deleteWriting(id)
      setWritings((ws) => ws?.filter((w) => w.id !== id) ?? null)
      toast('Writing deleted')
    } catch (e) {
      toast(errorText(e))
    } finally {
      setConfirmDelete(null)
    }
  }

  const all = writings ?? []
  const counts = {
    drafts: all.filter((w) => w.status === 'draft').length,
    published: all.filter((w) => w.status === 'published').length,
    reflections: all.filter((w) => w.kind === 'reflection').length,
  }
  const shown = all.filter((w) =>
    filter === 'all' ? true : filter === 'drafts' ? w.status === 'draft' : filter === 'published' ? w.status === 'published' : w.kind === 'reflection',
  )
  const chips: Array<[Filter, string]> = [
    ['all', 'All'],
    ['drafts', `Drafts · ${counts.drafts}`],
    ['published', `Published · ${counts.published}`],
    ['reflections', `Reflections · ${counts.reflections}`],
  ]

  return (
    <div className="min-h-dvh bg-background">
      <BurrowNav />
      <main className="flex justify-center px-4 pb-24 pt-10 sm:pt-14">
        <div className="flex w-full max-w-[880px] flex-col gap-7">
          <div className="flex items-end justify-between gap-4">
            <h1 className="m-0 font-display text-[clamp(1.7rem,3.5vw,2.4rem)] font-normal text-primary">Writings</h1>
            <div ref={menuRef} className="relative">
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                disabled={creating}
                onClick={() => setMenuOpen((o) => !o)}
                className={`${btn.primaryLg}`}
              >
                {creating ? 'Starting…' : 'New writing'} <span aria-hidden>▾</span>
              </button>
              {menuOpen && (
                <div role="menu" className="absolute right-0 top-11 z-20 flex w-[280px] flex-col gap-0.5 rounded-2xl border border-border/50 bg-card p-1.5 text-[13px] font-medium shadow-[var(--shadow-pop)]">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false)
                      void startBlank()
                    }}
                    className="flex flex-col gap-0.5 rounded-lg px-3 py-2.5 text-left text-foreground hover:bg-foreground/4"
                  >
                    <span>Blank writing</span>
                    <span className="text-[11px] font-normal text-muted-foreground">Start from nothing</span>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    disabled={!todayCount}
                    onClick={() => {
                      setMenuOpen(false)
                      void startReflection()
                    }}
                    className="flex flex-col gap-0.5 rounded-lg bg-accent/15 px-3 py-2.5 text-left hover:bg-accent/25 disabled:bg-transparent disabled:opacity-60"
                  >
                    <span className="text-primary">Today’s reflection</span>
                    <span className="text-[11px] font-normal text-muted-foreground">
                      {shortWeekdayDate(now)} ·{' '}
                      {todayCount === null ? '…' : todayCount === 0 ? 'nothing saved yet today' : `${todayCount} thought${todayCount === 1 ? '' : 's'} saved today`}
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="wr-chip-row flex gap-1.5" role="group" aria-label="Filter writings">
            {chips.map(([f, label]) => (
              <button
                key={f}
                type="button"
                aria-pressed={filter === f}
                onClick={() => setFilter(f)}
                className={chip(filter === f)}
              >
                {label}
              </button>
            ))}
          </div>

          {error ? (
            <p role="alert" className="m-0 text-[14px] text-primary">
              {error}
            </p>
          ) : writings === null ? (
            <Loading label="Gathering your writings…" />
          ) : shown.length === 0 ? (
            <div className="flex flex-col items-start gap-2 border-t border-border/60 py-10">
              <p className="m-0 font-display text-[22px] text-primary">{all.length ? 'Nothing here yet.' : 'Nothing written yet.'}</p>
              <p className="m-0 text-[13.5px] text-muted-foreground">Start from a blank page, or from what you saved today.</p>
            </div>
          ) : (
            <ul className="m-0 flex list-none flex-col p-0">
              {shown.map((w) => (
                <li key={w.id} className="group flex items-baseline justify-between gap-4 border-t border-border/60 py-[18px] last:border-b">
                  <Link to={`/w/${w.id}`} className="flex min-w-0 flex-col gap-1 text-foreground no-underline">
                    <span className="font-display text-[20px]/[1.25] text-primary transition-colors group-hover:text-accent sm:text-[22px]/[1.25]">
                      {w.title || 'Untitled'}
                    </span>
                    {w.subtitle && (
                      <span className="line-clamp-1 font-serif text-[15px]/[1.35] font-light italic text-muted-foreground">{w.subtitle}</span>
                    )}
                    <span className="mt-0.5 text-[13px] font-medium text-muted-foreground">{rowMeta(w, now)}</span>
                  </Link>
                  <div className="flex shrink-0 items-center gap-3 text-[13px] font-medium">
                    {confirmDelete === w.id ? (
                      <span className="flex items-center gap-2">
                        <span className="text-muted-foreground">Delete?</span>
                        <button type="button" onClick={() => void remove(w.id)} className="font-semibold text-primary">
                          Yes
                        </button>
                        <button type="button" onClick={() => setConfirmDelete(null)} className="text-muted-foreground">
                          No
                        </button>
                      </span>
                    ) : (
                      <>
                        {w.status === 'published' && profile?.handle && w.slug && (
                          <Link to={`/${profile.handle}/${w.slug}`} className="text-muted-foreground no-underline hover:text-primary">
                            View
                          </Link>
                        )}
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(w.id)}
                          className="text-muted-foreground/70 opacity-0 transition-opacity hover:text-primary focus:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
                        >
                          Delete
                        </button>
                        <span className={w.status === 'draft' ? 'text-accent' : 'text-muted-foreground'}>{statusLabel(w)}</span>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </div>
  )
}
